import { readCookie } from "@/lib/cookies";

// "processing" — bytes đã gửi hết lên server (xhr.upload.onprogress đã đạt
// 100%) nhưng server còn đang nén ảnh + đẩy lên MinIO trước khi trả response.
// Không có event chuẩn nào theo dõi được giai đoạn này (chỉ có upload progress
// của chính request, không phải toàn bộ round-trip) — tách trạng thái riêng để
// UI không hiện thanh progress đứng ở 100% trông như đã xong trong khi thực ra
// chưa (dễ gây hiểu lầm "tải lên không phản ánh đúng tốc độ thật").
export type UploadStatus = "queued" | "uploading" | "processing" | "success" | "error";

export type UploadItem = {
  id: string;
  fileName: string;
  fileSize: number;
  fileKind: "image" | "pdf";
  // Số file gộp trong 1 item — item.fileName chỉ hiện tên file ĐẦU (hoặc "N tệp"
  // khi > 1). Xem comment ở EnqueueInput.file về lý do gộp nhiều file/1 request.
  fileCount: number;
  status: UploadStatus;
  progress: number;
  error?: string;
  entityLabel: string;
  targetUrl: string;
};

export type EnqueueInput = {
  // 1 file HOẶC nhiều file gộp chung 1 request (1 FormData nhiều field cùng
  // tên) — dùng cho trường hợp thứ tự cuối cùng trên server phải khớp đúng
  // thứ tự client gửi lên (vd ảnh tài sản: server ghép mảng theo Promise.all,
  // giữ nguyên order input — xem app/api/v1/assets/[id]/images/route.ts).
  // N request riêng lẻ chạy song song (mỗi request 1 file) sẽ RACE nhau ở
  // bước ghi DB (array_cat), file nào request xong trước đứng trước — không
  // phản ánh đúng thứ tự người dùng chọn/sắp xếp. Gộp 1 request loại hẳn race
  // này, đổi lại mất progress bar riêng từng file (hiện chung 1 progress cho
  // cả batch — chấp nhận được, đổi lấy đúng thứ tự).
  file: File | File[];
  uploadUrl: string;
  fieldName: string;
  extraFields?: Record<string, string>;
  entityLabel: string;
  targetUrl: string;
  onSuccess?: (data: unknown) => void;
  // Chỉ gọi khi lỗi VĨNH VIỄN (đã hết lượt retry, xem MAX_ATTEMPTS) — dùng
  // cho enqueueUploadAndWait() bên dưới, để caller "chờ" biết chính xác lúc
  // nào item thất bại hẳn (không phải mỗi lần retry trung gian).
  onError?: (error: string) => void;
};

type PublicItem = UploadItem;
type Listener = (items: PublicItem[]) => void;

type InternalItem = UploadItem & {
  _files: File[];
  _uploadUrl: string;
  _fieldName: string;
  _extraFields?: Record<string, string>;
  _onSuccess?: (data: unknown) => void;
  _onError?: (error: string) => void;
  _attempts: number;
};

const MAX_ATTEMPTS = 3;
const MAX_CONCURRENCY = 3;
const MAX_ITEMS = 10;
const RETRY_BACKOFF_MS = [500, 1000];
const AUTO_DISMISS_MS = 90_000;

function fileKindOf(file: File): "image" | "pdf" {
  return file.type === "application/pdf" ? "pdf" : "image";
}

class UploadQueueStore {
  private items: InternalItem[] = [];
  private listeners = new Set<Listener>();
  private activeCount = 0;

  subscribe(fn: Listener) {
    this.listeners.add(fn);
  }

  unsubscribe(fn: Listener) {
    this.listeners.delete(fn);
  }

  // Snapshot đồng bộ trạng thái hiện tại — dùng cho component mount SAU khi
  // item đã được enqueue (vd panel chi tiết tài sản mở lại sau khi form tạo
  // mới đã enqueue ảnh) — subscribe() một mình không đủ vì chỉ nhận được item
  // MỚI phát sinh sau lúc đăng ký, bỏ lỡ item đang chạy từ trước.
  getItems(): PublicItem[] {
    return this.items.map(
      ({ _files, _uploadUrl, _fieldName, _extraFields, _onSuccess, _onError, _attempts, ...rest }) => rest
    );
  }

  enqueue(input: EnqueueInput): string {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const files = Array.isArray(input.file) ? input.file : [input.file];
    const item: InternalItem = {
      id,
      // Nhiều file gộp chung 1 request — liệt kê đúng tên từng file (nối bằng
      // ", ") thay vì chỉ ghi "N tệp", để người dùng biết chính xác ảnh nào
      // đang/đã tải lên. Nhưng UI chỉ hiện 1 DÒNG (CSS truncate) — tên dài/
      // nhiều file dễ bị cắt "..." ngay sau file ĐẦU, trông như chỉ có 1 file
      // dù thật ra vẫn tải đủ. Thêm số lượng "(N ảnh) " ở đầu để luôn thấy rõ
      // tổng số ngay cả khi phần tên sau đó bị cắt.
      fileName: files.length > 1
        ? `(${files.length} ${fileKindOf(files[0]) === "pdf" ? "tệp" : "ảnh"}) ${files.map((f) => f.name).join(", ")}`
        : files[0].name,
      fileSize: files.reduce((sum, f) => sum + f.size, 0),
      fileKind: fileKindOf(files[0]),
      fileCount: files.length,
      status: "queued",
      progress: 0,
      entityLabel: input.entityLabel,
      targetUrl: input.targetUrl,
      _files: files,
      _uploadUrl: input.uploadUrl,
      _fieldName: input.fieldName,
      _extraFields: input.extraFields,
      _onSuccess: input.onSuccess,
      _onError: input.onError,
      _attempts: 0,
    };
    this.items = [item, ...this.items];
    this.trimFinished();
    this.emit();
    this.pump();
    return id;
  }

  retry(id: string) {
    const item = this.items.find((i) => i.id === id);
    if (!item) return;
    item.status = "queued";
    item.progress = 0;
    item.error = undefined;
    item._attempts = 0;
    this.emit();
    this.pump();
  }

  dismiss(id: string) {
    this.items = this.items.filter((i) => i.id !== id);
    this.emit();
  }

  private trimFinished() {
    // Newest item is at index 0 (enqueue unshifts), so the oldest finished
    // item is at the end of this list — drop from the end, not the start.
    const finishedIdx = this.items
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => item.status === "success" || item.status === "error");
    const overflow = this.items.length - MAX_ITEMS;
    if (overflow <= 0) return;
    const toDrop = finishedIdx.slice(-overflow).map(({ idx }) => idx);
    this.items = this.items.filter((_, idx) => !toDrop.includes(idx));
  }

  private pump() {
    if (this.activeCount >= MAX_CONCURRENCY) return;
    // Newest item is at index 0 (enqueue unshifts, for newest-first display) —
    // process oldest-queued-first (FIFO) by searching from the end.
    const next = [...this.items].reverse().find((i) => i.status === "queued");
    if (!next) return;
    this.activeCount++;
    next.status = "uploading";
    this.emit();
    this.runUpload(next).finally(() => {
      this.activeCount--;
      this.pump();
    });
  }

  private async runUpload(item: InternalItem): Promise<void> {
    item._attempts++;
    const result = await this.sendXhr(item);
    if (result.ok) {
      item.status = "success";
      item.progress = 100;
      this.emit();
      item._onSuccess?.(result.data);
      setTimeout(() => this.dismiss(item.id), AUTO_DISMISS_MS);
      return;
    }
    // Lỗi 4xx (vd file quá dung lượng, sai định dạng) là lỗi VĨNH VIỄN — client
    // gửi lại y hệt vẫn sẽ bị từ chối y hệt, retry chỉ làm người dùng chờ vô ích
    // trước khi thấy lỗi thật. Chỉ retry lỗi mạng/server (5xx, mất kết nối).
    const isClientError = result.status !== undefined && result.status >= 400 && result.status < 500;
    if (!isClientError && item._attempts < MAX_ATTEMPTS) {
      await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS[item._attempts - 1] ?? 1000));
      // re-queue immediately — still counted under the same activeCount slot via this recursive call
      return this.runUpload(item);
    }
    item.status = "error";
    item.error = result.error ?? "Tải lên thất bại";
    this.emit();
    item._onError?.(item.error);
  }

  private sendXhr(item: InternalItem): Promise<{ ok: boolean; data?: unknown; status?: number; error?: string }> {
    return new Promise((resolve) => {
      const fd = new FormData();
      for (const f of item._files) fd.append(item._fieldName, f);
      if (item._extraFields) {
        for (const [k, v] of Object.entries(item._extraFields)) fd.append(k, v);
      }
      // item.id ổn định qua mọi lần retry của CHÍNH item này (retry() không tạo
      // id mới) — dùng luôn làm idempotency key để server chặn xử lý trùng nếu
      // 1 lần thử thực ra đã thành công ở server nhưng client nhận nhầm là lỗi
      // mạng (mất kết nối giữa lúc server xử lý xong và lúc response về tới
      // client) rồi tự retry, tránh tạo 2 bản ghi trùng nội dung. Xem
      // lib/idempotency.ts + app/api/v1/assets/[id]/invoice/route.ts.
      fd.append("idempotencyKey", item.id);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", item._uploadUrl);
      const csrf = readCookie("sws.csrf-token");
      if (csrf) xhr.setRequestHeader("x-csrf-token", csrf);
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        item.progress = Math.round((e.loaded / e.total) * 100);
        this.emit();
      };
      // Request body đã gửi xong (100% bytes) nhưng response chưa về — server
      // còn xử lý (nén ảnh, upload MinIO). Chuyển sang "processing" thay vì để
      // nguyên "uploading" ở progress=100%, tránh trông như đã xong.
      xhr.upload.onload = () => {
        item.status = "processing";
        this.emit();
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          let data: unknown;
          try { data = JSON.parse(xhr.responseText).data; } catch { /* ignore */ }
          resolve({ ok: true, data });
        } else {
          // Đọc thông báo lỗi thật từ server (vd "File X quá 15MB") thay vì chỉ
          // biết status code — hiện nguyên văn cho người dùng thấy lý do thật.
          let error: string | undefined;
          try { error = JSON.parse(xhr.responseText).error; } catch { /* ignore */ }
          resolve({ ok: false, status: xhr.status, error });
        }
      };
      xhr.onerror = () => resolve({ ok: false });
      xhr.send(fd);
    });
  }

  private emit() {
    const publicItems: PublicItem[] = this.items.map(
      ({ _files, _uploadUrl, _fieldName, _extraFields, _onSuccess, _onError, _attempts, ...rest }) => rest
    );
    this.listeners.forEach((fn) => fn(publicItems));
  }
}

export const uploadQueueStore = new UploadQueueStore();

export function enqueueUpload(input: EnqueueInput): string {
  return uploadQueueStore.enqueue(input);
}

// Enqueue nhưng vẫn ĐỢI kết quả (khác enqueueUpload — fire-and-forget) — dùng
// khi caller cần chạy tiếp bước phụ thuộc kết quả upload (vd PATCH sắp xếp
// lại ảnh sau khi upload xong, xem asset-form.tsx nhánh isEditMode) nhưng vẫn
// muốn hiện UploadProgressPopup thay vì chặn UI im lặng không có gì hiện ra.
// Không dùng retry() được cho item này qua nút trong popup — lỗi trả thẳng về
// caller để tự quyết định (giữ nguyên form mở cho thử lại, theo đúng hành vi
// đã có từ trước khi đổi sang dùng hàng đợi).
export function enqueueUploadAndWait(
  input: Omit<EnqueueInput, "onSuccess" | "onError">
): Promise<{ ok: boolean; data?: unknown; error?: string }> {
  return new Promise((resolve) => {
    uploadQueueStore.enqueue({
      ...input,
      onSuccess: (data) => resolve({ ok: true, data }),
      onError: (error) => resolve({ ok: false, error }),
    });
  });
}

export function retryUpload(id: string): void {
  uploadQueueStore.retry(id);
}

export function dismissUpload(id: string): void {
  uploadQueueStore.dismiss(id);
}
